import { doc, getDoc, updateDoc, limit } from 'firebase/firestore';
import { db } from './firebase';
import { ACHIEVEMENTS, getAchievementInfo } from './achievements';
import { HOST_ACHIEVEMENTS, getHostAchievementInfo } from './hostAchievements';
import { VENUE_ACHIEVEMENTS, getVenueAchievementInfo } from './venueAchievements';

/**
 * Get all available badges for a specific user type
 * @param {string} userType - 'team', 'host', or 'venue'
 * @returns {Object} - All badges organized by category
 */
export const getAllBadges = (userType) => {
  let achievements;
  let getInfoFunction;
  
  switch (userType) {
    case 'team':
      achievements = ACHIEVEMENTS;
      getInfoFunction = getAchievementInfo;
      break;
    case 'host':
      achievements = HOST_ACHIEVEMENTS;
      getInfoFunction = getHostAchievementInfo;
      break;
    case 'venue':
      achievements = VENUE_ACHIEVEMENTS;
      getInfoFunction = getVenueAchievementInfo;
      break;
    default:
      throw new Error('Invalid user type. Must be "team", "host", or "venue"');
  }

  // Organize badges by category
  const badgesByCategory = {};
  Object.values(achievements).forEach(badge => {
    if (!badgesByCategory[badge.category]) {
      badgesByCategory[badge.category] = [];
    }
    badgesByCategory[badge.category].push(badge);
  });

  return {
    allBadges: Object.values(achievements),
    badgesByCategory,
    getInfoFunction
  };
};

/**
 * Get current badges for a user/team/venue
 * @param {string} userId - User ID
 * @param {string} userType - 'team', 'host', or 'venue'
 * @returns {Promise<Array>} - Array of current badge IDs
 */
export const getCurrentBadges = async (userId, userType) => {
  try {
    // Determine which collection to use based on user type
    let collectionName = 'users';
    if (userType === 'team') {
      collectionName = 'teams';
    } else if (userType === 'venue') {
      collectionName = 'venues';
    }
    
    const docRef = doc(db, collectionName, userId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`${userType} not found`);
    }

    const data = docSnap.data();
    return data.achievements || [];
  } catch (error) {
    console.error(`Error getting current badges for ${userType}:`, error);
    throw error;
  }
};

/**
 * Award a badge to a user/team/venue
 * @param {string} userId - User ID
 * @param {string} userType - 'team', 'host', or 'venue'
 * @param {string} badgeId - Badge ID to award
 * @param {string} adminUserId - Admin user ID for audit trail
 * @returns {Promise<Object>} - Success/error result
 */
export const awardBadge = async (userId, userType, badgeId, adminUserId) => {
  try {
    // Validate badge exists
    const { allBadges } = getAllBadges(userType);
    const badgeExists = allBadges.some(badge => badge.id === badgeId);
    if (!badgeExists) {
      throw new Error(`Badge "${badgeId}" does not exist for ${userType} users`);
    }

    // Determine which collection to use based on user type
    let collectionName = 'users';
    if (userType === 'team') {
      collectionName = 'teams';
    } else if (userType === 'venue') {
      collectionName = 'venues';
    }

    // Get current data
    const docRef = doc(db, collectionName, userId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`${userType} not found`);
    }

    const data = docSnap.data();
    const currentBadges = data.achievements || [];

    // Check if already has the badge
    if (currentBadges.includes(badgeId)) {
      return {
        success: false,
        message: `${userType} already has this badge`
      };
    }

    // Add the badge
    const updatedBadges = [...currentBadges, badgeId];
    await updateDoc(docRef, {
      achievements: updatedBadges,
      lastBadgeUpdate: new Date(),
      lastBadgeUpdateBy: adminUserId
    });

    // Log the action
    await logBadgeAction(userId, userType, badgeId, 'awarded', adminUserId);

    return {
      success: true,
      message: `Badge "${badgeId}" awarded successfully to ${userType}`,
      newBadges: updatedBadges
    };
  } catch (error) {
    console.error(`Error awarding badge to ${userType}:`, error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Remove a badge from a user/team/venue
 * @param {string} userId - User ID
 * @param {string} userType - 'team', 'host', or 'venue'
 * @param {string} badgeId - Badge ID to remove
 * @param {string} adminUserId - Admin user ID for audit trail
 * @returns {Promise<Object>} - Success/error result
 */
export const removeBadge = async (userId, userType, badgeId, adminUserId) => {
  try {
    // Determine which collection to use based on user type
    let collectionName = 'users';
    if (userType === 'team') {
      collectionName = 'teams';
    } else if (userType === 'venue') {
      collectionName = 'venues';
    }

    // Get current data
    const docRef = doc(db, collectionName, userId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`${userType} not found`);
    }

    const data = docSnap.data();
    const currentBadges = data.achievements || [];

    // Check if has the badge
    if (!currentBadges.includes(badgeId)) {
      return {
        success: false,
        message: `${userType} does not have this badge`
      };
    }

    // Remove the badge
    const updatedBadges = currentBadges.filter(badge => badge !== badgeId);
    await updateDoc(docRef, {
      achievements: updatedBadges,
      lastBadgeUpdate: new Date(),
      lastBadgeUpdateBy: adminUserId
    });

    // Log the action
    await logBadgeAction(userId, userType, badgeId, 'removed', adminUserId);

    return {
      success: true,
      message: `Badge "${badgeId}" removed successfully from ${userType}`,
      newBadges: updatedBadges
    };
  } catch (error) {
    console.error(`Error removing badge from ${userType}:`, error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Award multiple badges at once
 * @param {string} userId - User ID
 * @param {string} userType - 'team', 'host', or 'venue'
 * @param {Array<string>} badgeIds - Array of badge IDs to award
 * @param {string} adminUserId - Admin user ID for audit trail
 * @returns {Promise<Object>} - Success/error result
 */
export const awardMultipleBadges = async (userId, userType, badgeIds, adminUserId) => {
  try {
    // Validate all badges exist
    const { allBadges } = getAllBadges(userType);
    const invalidBadges = badgeIds.filter(badgeId => 
      !allBadges.some(badge => badge.id === badgeId)
    );
    
    if (invalidBadges.length > 0) {
      throw new Error(`Invalid badges: ${invalidBadges.join(', ')}`);
    }

    // Determine which collection to use based on user type
    let collectionName = 'users';
    if (userType === 'team') {
      collectionName = 'teams';
    } else if (userType === 'venue') {
      collectionName = 'venues';
    }

    // Get current data
    const docRef = doc(db, collectionName, userId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`${userType} not found`);
    }

    const data = docSnap.data();
    const currentBadges = data.achievements || [];

    // Add new badges (avoid duplicates)
    const newBadges = badgeIds.filter(badgeId => !currentBadges.includes(badgeId));
    const updatedBadges = [...currentBadges, ...newBadges];

    if (newBadges.length === 0) {
      return {
        success: false,
        message: `${userType} already has all these badges`
      };
    }

    await updateDoc(docRef, {
      achievements: updatedBadges,
      lastBadgeUpdate: new Date(),
      lastBadgeUpdateBy: adminUserId
    });

    // Log each badge action
    for (const badgeId of newBadges) {
      await logBadgeAction(userId, userType, badgeId, 'awarded', adminUserId);
    }

    return {
      success: true,
      message: `Awarded ${newBadges.length} new badges to ${userType}`,
      newBadges: updatedBadges,
      awardedBadges: newBadges
    };
  } catch (error) {
    console.error(`Error awarding multiple badges to ${userType}:`, error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Remove multiple badges at once
 * @param {string} userId - User ID
 * @param {string} userType - 'team', 'host', or 'venue'
 * @param {Array<string>} badgeIds - Array of badge IDs to remove
 * @param {string} adminUserId - Admin user ID for audit trail
 * @returns {Promise<Object>} - Success/error result
 */
export const removeMultipleBadges = async (userId, userType, badgeIds, adminUserId) => {
  try {
    // Determine which collection to use based on user type
    let collectionName = 'users';
    if (userType === 'team') {
      collectionName = 'teams';
    } else if (userType === 'venue') {
      collectionName = 'venues';
    }

    // Get current data
    const docRef = doc(db, collectionName, userId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`${userType} not found`);
    }

    const data = docSnap.data();
    const currentBadges = data.achievements || [];

    // Remove specified badges
    const updatedBadges = currentBadges.filter(badge => !badgeIds.includes(badge));
    const removedBadges = currentBadges.filter(badge => badgeIds.includes(badge));

    if (removedBadges.length === 0) {
      return {
        success: false,
        message: `${userType} does not have any of these badges`
      };
    }

    await updateDoc(docRef, {
      achievements: updatedBadges,
      lastBadgeUpdate: new Date(),
      lastBadgeUpdateBy: adminUserId
    });

    // Log each badge action
    for (const badgeId of removedBadges) {
      await logBadgeAction(userId, userType, badgeId, 'removed', adminUserId);
    }

    return {
      success: true,
      message: `Removed ${removedBadges.length} badges from ${userType}`,
      newBadges: updatedBadges,
      removedBadges: removedBadges
    };
  } catch (error) {
    console.error(`Error removing multiple badges from ${userType}:`, error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Log badge actions for audit trail
 * @param {string} userId - User ID
 * @param {string} userType - 'team', 'host', or 'venue'
 * @param {string} badgeId - Badge ID
 * @param {string} action - 'awarded' or 'removed'
 * @param {string} adminUserId - Admin user ID
 */
const logBadgeAction = async (userId, userType, badgeId, action, adminUserId) => {
  try {
    const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
    
    await addDoc(collection(db, 'badgeAuditLog'), {
      userId,
      userType,
      badgeId,
      action,
      adminUserId,
      timestamp: serverTimestamp(),
      date: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error logging badge action:', error);
  }
};

/**
 * Get badge audit log for a specific user
 * @param {string} userId - User ID
 * @param {string} userType - 'team', 'host', or 'venue'
 * @returns {Promise<Array>} - Array of audit log entries
 */
export const getBadgeAuditLog = async (userId, userType) => {
  try {
    const { collection, query, where, orderBy, getDocs } = await import('firebase/firestore');
    
    const auditQuery = query(
      collection(db, 'badgeAuditLog'),
      where('userId', '==', userId),
      where('userType', '==', userType),
      orderBy('timestamp', 'desc')
    );
    
    const snapshot = await getDocs(auditQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting badge audit log:', error);
    return [];
  }
};

/**
 * Search for users/teams/venues by name or email
 * @param {string} searchTerm - Search term
 * @param {string} userType - 'team', 'host', or 'venue'
 * @returns {Promise<Array>} - Array of matching users
 */
export const searchUsers = async (searchTerm, userType) => {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    
    switch (userType) {
      case 'team':
        // Search teams by name, league ID, captain email, or partial matches
        let teamResults = [];
        
        // 1. Search by team name (prefix and partial match)
        try {
          const teamNameQuery = query(
            collection(db, 'teams'),
            where('name', '>=', searchTerm),
            where('name', '<=', searchTerm + '\uf8ff')
          );
          
          const teamNameSnapshot = await getDocs(teamNameQuery);
          const nameResults = teamNameSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          teamResults = [...teamResults, ...nameResults];
        } catch (error) {
          console.log('Team name search failed:', error);
        }

        // 2. Search by league ID (exact and partial match)
        try {
          const leagueIdQuery = query(
            collection(db, 'teams'),
            where('leagueId', '>=', searchTerm.toUpperCase()),
            where('leagueId', '<=', searchTerm.toUpperCase() + '\uf8ff')
          );
          
          const leagueIdSnapshot = await getDocs(leagueIdQuery);
          const leagueResults = leagueIdSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          teamResults = [...teamResults, ...leagueResults];
        } catch (error) {
          console.log('League ID search failed:', error);
        }

        // 3. Search by captain email
        try {
          const captainEmailQuery = query(
            collection(db, 'teams'),
            where('captainEmail', '>=', searchTerm),
            where('captainEmail', '<=', searchTerm + '\uf8ff')
          );
          
          const captainEmailSnapshot = await getDocs(captainEmailQuery);
          const emailResults = captainEmailSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          teamResults = [...teamResults, ...emailResults];
        } catch (error) {
          console.log('Captain email search failed:', error);
        }

        // 4. Search by home venue (partial match)
        try {
          const venueQuery = query(
            collection(db, 'teams'),
            where('homeVenue', '>=', searchTerm),
            where('homeVenue', '<=', searchTerm + '\uf8ff')
          );
          
          const venueSnapshot = await getDocs(venueQuery);
          const venueResults = venueSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          teamResults = [...teamResults, ...venueResults];
        } catch (error) {
          console.log('Home venue search failed:', error);
        }

        // 5. Fallback: Get all teams and filter locally for partial matches
        // This handles cases where Firestore indexes don't exist
        try {
          const allTeamsQuery = query(collection(db, 'teams'), limit(100));
          const allTeamsSnapshot = await getDocs(allTeamsQuery);
          const allTeams = allTeamsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          const searchTermLower = searchTerm.toLowerCase();
          const localResults = allTeams.filter(team => {
            const nameMatch = team.name?.toLowerCase().includes(searchTermLower);
            const leagueIdMatch = team.leagueId?.toLowerCase().includes(searchTermLower);
            const captainEmailMatch = team.captainEmail?.toLowerCase().includes(searchTermLower);
            const venueMatch = team.homeVenue?.toLowerCase().includes(searchTermLower);
            
            return nameMatch || leagueIdMatch || captainEmailMatch || venueMatch;
          });
          
          teamResults = [...teamResults, ...localResults];
        } catch (error) {
          console.log('Local team search failed:', error);
        }

        // Remove duplicates and limit results
        const uniqueTeamResults = teamResults.filter((item, index, self) => 
          index === self.findIndex(t => t.id === item.id)
        );

        return uniqueTeamResults.slice(0, 10);

      case 'host':
        // Search users by email (primary) and displayName (secondary)
        const hostEmailQuery = query(
          collection(db, 'users'),
          where('email', '>=', searchTerm),
          where('email', '<=', searchTerm + '\uf8ff')
        );
        
        const hostEmailSnapshot = await getDocs(hostEmailQuery);
        let hostResults = hostEmailSnapshot.docs
          .filter(doc => {
            const data = doc.data();
            // Only include users with host-related roles
            return data.role === 'host' || data.role === 'admin';
          })
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

        // Also search by displayName if it exists
        try {
          const hostNameQuery = query(
            collection(db, 'users'),
            where('displayName', '>=', searchTerm),
            where('displayName', '<=', searchTerm + '\uf8ff')
          );
          
          const hostNameSnapshot = await getDocs(hostNameQuery);
          const hostNameResults = hostNameSnapshot.docs
            .filter(doc => {
              const data = doc.data();
              return data.role === 'host' || data.role === 'admin';
            })
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
          
          // Merge and remove duplicates
          const allHostResults = [...hostResults, ...hostNameResults];
          hostResults = allHostResults.filter((item, index, self) => 
            index === self.findIndex(t => t.id === item.id)
          );
        } catch (error) {
          console.log('DisplayName search failed, using email search only');
        }

        return hostResults.slice(0, 10);

      case 'venue':
        // Search venues by name
        const venueQuery = query(
          collection(db, 'venues'),
          where('name', '>=', searchTerm),
          where('name', '<=', searchTerm + '\uf8ff')
        );
        
        const venueSnapshot = await getDocs(venueQuery);
        const venueResults = venueSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        return venueResults.slice(0, 10);

      default:
        throw new Error('Invalid user type');
    }
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
}; 